import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { CollapsibleSection } from "components/designV2/collapsibleSection/collapsibleSection";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { InfoText } from "components/designV2/infoText/infoText";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { StatusBanner } from "components/designV2/statusBanner/statusBanner";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import { ColorV2 } from "config/colors";
import { useWithdrawTimeReport } from "hooks/useWithdrawTimeReport";
import { CompanyGigShift, GigType } from "model/Gig";
import { AbsenceType, ScheduleStatus } from "model/Schedule";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { schedule as scheduleService } from "services/api/company/schedule.service";
import { parseNonNegativeDecimalInput } from "utils/input.normalize.logic";
import type { Result } from "utils/result";
import { isOk, ok } from "utils/result";
import { ErrorModal } from "web-apps/company/components/common/errorModal.component";
import type {
  ScheduleSection,
  ScheduleShiftItem,
  ScheduleWorkerRow,
} from "web-apps/company/components/schedule/types/schedule.types";
import {
  formatBreakRangeLabel,
  formatDateLabel,
  formatTimeRange,
  formatTotalTime,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import {
  getBlockStatusStyling,
  getBlockStatusText,
  getShiftDetailTexts,
} from "web-apps/company/components/schedule/utils/schedule.status";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { canEditShiftByFourthRule } from "web-apps/company/utils/dateRules";
import { getAbsenceTypeLabel } from "web-apps/company/utils/absenceLabels";
import { useShiftDetailsEdit } from "../../hooks/useShiftDetailsEdit";
import { useWorkerAvatar } from "../../hooks/useWorkerAvatar";
import { DeclineModal } from "./declineModal.component";
import { ReplaceModal } from "./replaceModal.component";
import { ReportAbsenceModalV2 } from "web-apps/company/components/common/modals/reportAbsenceModalV2.component";
import {
  DeductionEditBlock,
  DetailBlock,
  DetailLabel,
  DetailValue,
  DetailsLoader,
  DetailsMessage,
  DetailsRow,
  DetailsSpinner,
  EditActions,
  EditDetailsRow,
  EditFooterRow,
  EditTimeRow,
  EditTotalsRow,
  FooterNote,
  ModalContent,
  RoleDetailsRow,
  SectionTitle,
} from "./shiftDetailsModal.styled";

const REMOVE_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.NegativeRed,
  hoverBackgroundColor: ColorV2.DeclinedRed,
  activeBackgroundColor: ColorV2.DeclinedRed,
  textColor: ColorV2.White,
  borderColor: "transparent",
  iconColor: ColorV2.White,
};

const ABSENCE_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  borderColor: ColorV2.NegativeRed,
  textColor: ColorV2.NegativeRed,
  iconColor: ColorV2.NegativeRed,
  hoverBackgroundColor: "rgba(211, 78, 78, 0.12)",
  activeBackgroundColor: "rgba(211, 78, 78, 0.2)",
};

interface ShiftDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAbsenceReported?: () => void;
  onWithdrawCompleted?: () => void;
  onReplaceSuccess?: () => void;
  status: ScheduleStatus;
  worker: ScheduleWorkerRow;
  gigRole?: string | null;
  selectedShift: ScheduleShiftItem;
  section: ScheduleSection;
  onShiftEdited?: (next: ShiftDetails) => void;
  shiftDetails?: ShiftDetails | null;
  isShiftDetailsLoading?: boolean;
  shiftDetailsError?: boolean;
}

export type ShiftDetails = {
  startTime: string;
  endTime: string;
  breakTime: number;
  breakStartTime: string;
  breakEndTime: string;
  deductionAmount: number;
};

type OverlappingShiftsResponse = {
  shifts: CompanyGigShift[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const resolveApiErrorMessage = (value: unknown): string | null => {
  if (!isRecord(value)) return null;
  const response = isRecord(value.response) ? value.response : null;
  const data = response && isRecord(response.data) ? response.data : null;
  const message =
    data && typeof data.errorMessage === "string" ? data.errorMessage : null;
  return message;
};

const parseOverlappingShifts = (
  raw: CompanyGigShift[] | OverlappingShiftsResponse,
): Result<CompanyGigShift[], "invalid-data"> =>
  Array.isArray(raw) ? ok(raw) : ok(raw.shifts);

const mapEditErrorKey = (value?: string | null): string => {
  const allowedKeys = new Set([
    "EditShiftOverlapError",
    "GigShift not found",
    "GigShift can not be updated",
  ]);
  if (value && allowedKeys.has(value)) return value;
  return "GigShift can not be updated";
};

const normalizeWorkerRole = (
  value: string | null | undefined,
  translate: (key: string) => string,
): string | null => {
  if (!value) return null;
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0) return null;
  const lowerCasedValue = trimmedValue.toLowerCase();
  if (lowerCasedValue === "undefined" || lowerCasedValue === "unknown") {
    return translate("Schedule.UnknownValue");
  }
  return trimmedValue;
};

type ModalState =
  | { kind: "none" }
  | { kind: "absence" }
  | { kind: "decline" }
  | { kind: "replace" }
  | { kind: "withdrawError" }
  | { kind: "editError"; errorKey: string };

export const ShiftDetailsModal: React.FC<ShiftDetailsModalProps> = (props) => {
  const { t, i18n } = useTranslation();
  const [companyState] = useCompanyStore();
  const {
    withdrawTimeReport,
    isLoading: isWithdrawing,
    error,
  } = useWithdrawTimeReport();
  const [modalState, setModalState] = useState<ModalState>({ kind: "none" });
  const [isSaving, setIsSaving] = useState(false);

  const closeModal = (): void => {
    setModalState({ kind: "none" });
  };

  const workerName =
    props.worker.isWorker === false
      ? t(`Schedule.${props.worker.heading}`, {
          defaultValue: props.worker.heading,
        })
      : props.worker.heading;
  const shiftWorkerName =
    props.selectedShift.workerId != null && props.selectedShift.workerName
      ? props.selectedShift.workerName.trim()
      : null;

  const shiftWorkerExternalId = props.selectedShift.workerExternalId ?? null;

  const isTeamOrPublicShift =
    props.selectedShift.gigType === GigType.PUBLIC ||
    props.selectedShift.gigType === GigType.WORKPOOL ||
    props.selectedShift.gigType === GigType.WORKGROUP;

  const hasShiftWorkerIdentity =
    props.selectedShift.workerId != null ||
    Boolean(shiftWorkerName) ||
    Boolean(shiftWorkerExternalId);

  const shouldShowShiftWorker = isTeamOrPublicShift && hasShiftWorkerIdentity;

  const displayWorkerName: string =
    shouldShowShiftWorker && shiftWorkerName ? shiftWorkerName : workerName;

  const workerRole = normalizeWorkerRole(props.worker.text, t);
  const workerSectionText = props.section.text ?? null;
  const normalizedWorkerRole = workerRole?.trim().toLowerCase() ?? null;

  const normalizedWorkerSectionText =
    workerSectionText?.trim().toLowerCase() ?? null;

  const workerTags =
    workerSectionText && normalizedWorkerRole !== normalizedWorkerSectionText
      ? [workerSectionText]
      : [];

  const workerExternalId = shouldShowShiftWorker
    ? shiftWorkerExternalId
    : (props.worker.externalId ?? null);

  const isWorker = props.worker.isWorker ?? null;
  const paymentType = props.worker.paymentType ?? null;
  const workerId = props.worker.id;
  const workGroupId = props.section.id;
  const gigId = props.selectedShift.gigId;
  const gigShiftId = props.selectedShift.gigShiftId;
  const timeReportId = props.selectedShift.details?.[0]?.timeReportId ?? null;
  const isNonWorker = isWorker === false && !shouldShowShiftWorker;
  const gigRole = props.gigRole?.trim() || null;

  const statusStyling = getBlockStatusStyling(props.status);
  const statusLabel = getBlockStatusText(props.status, t);
  const statusDetails = getShiftDetailTexts(props.status, t);
  const absenceLabels = useMemo<Record<AbsenceType, string>>(
    () => ({
      [AbsenceType.SICK_LEAVE]: getAbsenceTypeLabel(AbsenceType.SICK_LEAVE, t),
      [AbsenceType.SICK_LEAVE_CHILD]: getAbsenceTypeLabel(
        AbsenceType.SICK_LEAVE_CHILD,
        t,
      ),
      [AbsenceType.VACATION]: getAbsenceTypeLabel(AbsenceType.VACATION, t),
      [AbsenceType.PARENTAL_LEAVE]: getAbsenceTypeLabel(
        AbsenceType.PARENTAL_LEAVE,
        t,
      ),
      [AbsenceType.LEAVE_OF_ABSENCE]: getAbsenceTypeLabel(
        AbsenceType.LEAVE_OF_ABSENCE,
        t,
      ),
      [AbsenceType.OTHER]: getAbsenceTypeLabel(AbsenceType.OTHER, t),
      [AbsenceType.CANCELLED_BY_EMPLOYER]: getAbsenceTypeLabel(
        AbsenceType.CANCELLED_BY_EMPLOYER,
        t,
      ),
    }),
    [t],
  );

  //TODO: when we have support for reporting absence for parts of the day, then we cannot just use index 0
  const absenceResolutionType = props.selectedShift.details?.[0]?.resolution;

  const absenceResolutionLabel = absenceResolutionType
    ? absenceLabels[absenceResolutionType]
    : null;

  const baseStatusLabel = statusDetails.label ?? statusLabel;

  const showAbsenceResolution =
    (props.status === ScheduleStatus.PLANNED_ABSENCE ||
      props.status === ScheduleStatus.UNPLANNED_ABSENCE) &&
    Boolean(absenceResolutionLabel);

  const statusBannerLabel = showAbsenceResolution
    ? `${baseStatusLabel}: ${absenceResolutionLabel}`
    : baseStatusLabel;

  const showActions =
    props.status === ScheduleStatus.OPEN ||
    props.status === ScheduleStatus.PENDING ||
    props.status === ScheduleStatus.ACCEPTED;

  const showReplaceAction =
    props.status === ScheduleStatus.WITHDRAWN ||
    props.status === ScheduleStatus.DECLINED ||
    props.status === ScheduleStatus.PLANNED_ABSENCE ||
    props.status === ScheduleStatus.UNPLANNED_ABSENCE;

  const primaryActionLabel =
    props.status === ScheduleStatus.ACCEPTED
      ? t("Schedule.Withdraw")
      : t("Schedule.RemoveShift");
  const showAbsenceAction = props.status === ScheduleStatus.ACCEPTED;
  const footerNoteKey =
    props.status === ScheduleStatus.REPORTED
      ? "Schedule.ShiftCompleted"
      : props.status === ScheduleStatus.SUBMITTED
        ? "Schedule.Submitted"
        : null;

  const showFooterNote =
    Boolean(footerNoteKey) && !showActions && !showReplaceAction;

  const detailsStartTime =
    props.shiftDetails?.startTime ?? props.selectedShift.startTime;
  const detailsEndTime =
    props.shiftDetails?.endTime ?? props.selectedShift.endTime;

  const timeRange = formatTimeRange(
    detailsStartTime,
    detailsEndTime,
    i18n.language,
  );

  const canEditShift =
    paymentType != null &&
    canEditShiftByFourthRule(detailsStartTime, [paymentType]);

  const breakRange = formatBreakRangeLabel({
    breakStartTime: props.shiftDetails?.breakStartTime ?? null,
    breakEndTime: props.shiftDetails?.breakEndTime ?? null,
    breakTime: props.shiftDetails?.breakTime ?? null,
    locale: i18n.language,
  });

  const totalTime =
    props.shiftDetails?.breakTime !== undefined
      ? formatTotalTime(
          detailsStartTime,
          detailsEndTime,
          props.shiftDetails.breakTime,
        )
      : undefined;
  const deductionAmount = props.shiftDetails?.deductionAmount;

  const avatarUrl = useWorkerAvatar({
    workerExternalId,
    isWorker: shouldShowShiftWorker ? true : isWorker,
    initialUrl: null,
  });

  const editState = useShiftDetailsEdit({
    detailsStartTime,
    detailsEndTime,
    breakStartTime: props.shiftDetails?.breakStartTime ?? null,
    breakEndTime: props.shiftDetails?.breakEndTime ?? null,
    deductionAmount,
    totalTime,
    isSaving,
  });

  const handleReplaceSuccess = (): void => {
    closeModal();
    props.onReplaceSuccess?.();
  };

  const handleSaveEdit = async (): Promise<void> => {
    if (!editState.editDerived || !editState.editDate || isSaving) return;
    const companyId = companyState.company?.id;
    if (!companyId) return;
    if (
      !editState.editStartTime ||
      !editState.editEndTime ||
      !editState.editBreakStartTime ||
      !editState.editBreakEndTime ||
      !editState.editDeductionAmount
    ) {
      return;
    }
    const deductionAmountValue = parseNonNegativeDecimalInput(
      editState.editDeductionAmount,
    );

    const payload: ShiftDetails = {
      startTime: editState.editDerived.startDate.toISOString(),
      endTime: editState.editDerived.endDate.toISOString(),
      breakTime: editState.editDerived.breakMinutes,
      breakStartTime: editState.editDerived.breakStartDate.toISOString(),
      breakEndTime: editState.editDerived.breakEndDate.toISOString(),
      deductionAmount: deductionAmountValue,
    };

    const overlapShift: CompanyGigShift = {
      startTime: payload.startTime,
      endTime: payload.endTime,
      breakTime: payload.breakTime,
      breakStartTime: payload.breakStartTime,
      breakEndTime: payload.breakEndTime,
      deductionAmount: payload.deductionAmount,
      id: gigShiftId,
    };

    const overlapData = {
      workerId,
      workGroupId,
      description: "",
      shifts: [overlapShift],
      type: GigType.SOLO,
    };

    setIsSaving(true);
    try {
      const overlapResponse = await Api().company.gig.getOverlappingGig(
        companyId,
        overlapData,
      );
      const parsedOverlap = parseOverlappingShifts(overlapResponse.data);
      const overlappingShifts = isOk(parsedOverlap) ? parsedOverlap.value : [];

      if (overlappingShifts.length > 0) {
        setModalState({
          kind: "editError",
          errorKey: "EditShiftOverlapError",
        });
        editState.setIsEditing(false);
        return;
      }

      await scheduleService.editScheduleShift(
        companyId,
        gigId,
        gigShiftId,
        payload,
      );
      props.onShiftEdited?.(payload);
      editState.setIsEditing(false);
    } catch (error) {
      const resolvedMessage = resolveApiErrorMessage(error);
      const message = mapEditErrorKey(resolvedMessage);
      setModalState({ kind: "editError", errorKey: message });
      editState.setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleWithdraw = async (comment: string): Promise<boolean> => {
    const companyId = companyState.company?.id;
    if (!companyId || !timeReportId || isWithdrawing) return false;
    const result = await withdrawTimeReport({
      companyId,
      timeReportId,
      companyNotes: comment,
    });
    if (!result.ok) {
      setModalState({ kind: "withdrawError" });
      return false;
    }
    props.onWithdrawCompleted?.();
    return true;
  };

  const handleClose = (): void => {
    setModalState({ kind: "none" });
    editState.setIsEditing(false);
    props.onClose();
  };

  return (
    <Modal
      label={t("Schedule.ShiftDetails")}
      isOpen={props.isOpen}
      onClose={handleClose}
      size="medium"
      footer={
        <>
          {showActions && (
            <>
              {!editState.isEditing && canEditShift && (
                <SecondaryButton
                  label={t("Schedule.EditShift")}
                  onClick={editState.handleStartEdit}
                />
              )}
              {showAbsenceAction && (
                <SecondaryButton
                  label={t("Schedule.Absence")}
                  styleOverrides={ABSENCE_BUTTON_STYLES}
                  onClick={() => setModalState({ kind: "absence" })}
                />
              )}
              <PrimaryButton
                label={primaryActionLabel}
                iconName="v2/cross"
                styleOverrides={REMOVE_BUTTON_STYLES}
                onClick={() => setModalState({ kind: "decline" })}
              />
            </>
          )}
          {showReplaceAction && (
            <SecondaryButton
              label={t("Schedule.Replace")}
              onClick={() => setModalState({ kind: "replace" })}
            />
          )}
          {showFooterNote && footerNoteKey && (
            <FooterNote>{t(footerNoteKey)}</FooterNote>
          )}
        </>
      }
    >
      <ModalContent>
        <SectionTitle>{t("Schedule.Worker")}</SectionTitle>
        <AvatarChip
          title={displayWorkerName}
          subTitle={workerRole}
          tags={workerTags}
          avatarUrl={avatarUrl}
          iconName={isNonWorker ? "v2/shift" : undefined}
          avatarBackgroundColor={isNonWorker ? ColorV2.Blue6 : undefined}
        />

        <StatusBanner
          iconName="v2/plus"
          label={statusBannerLabel}
          subLabel={statusDetails.text}
          textColor={statusStyling.color}
          backgroundColor={statusStyling.backgroundColor}
          fullWidth
        />

        <CollapsibleSection label={t("Schedule.ShiftDetails")} defaultOpen>
          {props.isShiftDetailsLoading ? (
            <DetailsLoader>
              <DetailsSpinner />
            </DetailsLoader>
          ) : props.shiftDetailsError ? (
            <DetailsMessage>
              {t("Schedule.ShiftDetailsNotFound")}
            </DetailsMessage>
          ) : (
            <>
              {gigRole && (
                <RoleDetailsRow>
                  <DetailBlock>
                    <DetailValue>{gigRole}</DetailValue>
                    <DetailLabel>{t("Schedule.GigRole")}</DetailLabel>
                  </DetailBlock>
                </RoleDetailsRow>
              )}
              {editState.isEditing ? (
                <>
                  <EditDetailsRow>
                    <DetailBlock>
                      <DatePicker
                        value={editState.editDate}
                        onChange={editState.setEditDate}
                        size="compact"
                      />
                      <DetailLabel>{t("Schedule.Date")}</DetailLabel>
                    </DetailBlock>
                    <DetailBlock>
                      <EditTimeRow>
                        <TimePicker
                          value={editState.editStartTime}
                          onChange={editState.setEditStartTime}
                          size="compact"
                        />
                        <TimePicker
                          value={editState.editEndTime}
                          onChange={editState.setEditEndTime}
                          size="compact"
                        />
                      </EditTimeRow>
                      <DetailLabel>{t("Schedule.PlannedStartEnd")}</DetailLabel>
                    </DetailBlock>
                    <DetailBlock>
                      <EditTimeRow>
                        <TimePicker
                          value={editState.editBreakStartTime}
                          onChange={editState.setEditBreakStartTime}
                          size="compact"
                        />
                        <TimePicker
                          value={editState.editBreakEndTime}
                          onChange={editState.setEditBreakEndTime}
                          size="compact"
                        />
                      </EditTimeRow>
                      <InfoText
                        text={t("Schedule.Break")}
                        tooltipText={t("Schedule.BreakInfo")}
                        textColor={ColorV2.Blue4}
                        textStyle="s1"
                      />
                    </DetailBlock>
                  </EditDetailsRow>
                  <EditTotalsRow>
                    <DeductionEditBlock>
                      <InputField
                        value={editState.editDeductionAmount}
                        onChange={editState.setEditDeductionAmount}
                        onBlur={editState.handleEditDeductionAmountBlur}
                        type="number"
                        maxLength={5}
                        size="compact"
                      />
                      <DetailLabel>{t("Schedule.Deduction")}</DetailLabel>
                    </DeductionEditBlock>
                  </EditTotalsRow>
                </>
              ) : (
                <DetailsRow>
                  <DetailBlock>
                    <DetailValue>
                      {formatDateLabel(detailsStartTime, i18n.language)}
                    </DetailValue>
                    <DetailLabel>{t("Schedule.Date")}</DetailLabel>
                  </DetailBlock>
                  <DetailBlock>
                    <DetailValue>{timeRange}</DetailValue>
                    <DetailLabel>{t("Schedule.PlannedStartEnd")}</DetailLabel>
                  </DetailBlock>
                  <DetailBlock>
                    <DetailValue>{breakRange}</DetailValue>
                    <InfoText
                      text={t("Schedule.Break")}
                      tooltipText={t("Schedule.BreakInfo")}
                      textColor={ColorV2.Blue4}
                      textStyle="s1"
                    />
                  </DetailBlock>
                  <DetailBlock>
                    <DetailValue>
                      {deductionAmount != null
                        ? `${deductionAmount} kr`
                        : "0 kr"}
                    </DetailValue>
                    <DetailLabel>{t("Schedule.Deduction")}</DetailLabel>
                  </DetailBlock>
                  <DetailBlock>
                    <DetailValue>{totalTime ?? "--"}</DetailValue>
                    <DetailLabel>{t("Schedule.TotalTime")}</DetailLabel>
                  </DetailBlock>
                </DetailsRow>
              )}
              {editState.isEditing && (
                <EditFooterRow>
                  <DetailBlock>
                    <DetailValue>{editState.editTotalTime ?? 0}</DetailValue>
                    <DetailLabel>{t("Schedule.TotalTime")}</DetailLabel>
                  </DetailBlock>
                  <EditActions>
                    <SecondaryButton
                      size="small"
                      label={t("General.Cancel")}
                      onClick={editState.handleCancelEdit}
                    />
                    <PrimaryButton
                      size="small"
                      label={t("General.Save")}
                      onClick={handleSaveEdit}
                      disabled={!editState.canSaveEdit}
                      isLoading={isSaving}
                    />
                  </EditActions>
                </EditFooterRow>
              )}
            </>
          )}
        </CollapsibleSection>
      </ModalContent>
      <ReportAbsenceModalV2
        isOpen={modalState.kind === "absence"}
        onClose={closeModal}
        timeReportId={timeReportId}
        onReportAbsence={() => props.onAbsenceReported?.()}
      />
      <DeclineModal
        isOpen={modalState.kind === "decline"}
        onClose={closeModal}
        onDecline={handleWithdraw}
        isSubmitting={isWithdrawing}
      />
      {error && modalState.kind === "withdrawError" && (
        <ErrorModal isOpen onClose={closeModal} messageKey={error} />
      )}
      {modalState.kind === "editError" && (
        <ErrorModal
          isOpen
          onClose={closeModal}
          titleKey="Schedule.EditShiftErrorTitle"
          messageKey={`Schedule.${modalState.errorKey}`}
        />
      )}
      <ReplaceModal
        isOpen={modalState.kind === "replace"}
        onClose={closeModal}
        onReplaceSuccess={handleReplaceSuccess}
        companyId={companyState.company?.id ?? null}
        workerId={workerId}
        workGroupId={workGroupId}
        startTime={detailsStartTime}
        endTime={detailsEndTime}
        breakStartTime={props.shiftDetails?.breakStartTime ?? null}
        breakEndTime={props.shiftDetails?.breakEndTime ?? null}
        breakTime={props.shiftDetails?.breakTime ?? null}
        deductionAmount={props.shiftDetails?.deductionAmount ?? null}
      />
    </Modal>
  );
};
